import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Application } from '../models/application/application';
import { ApplicationSendResponse } from '../models/application/applicationSendResponse';
import { BaseService } from './base.service';
import { FormService } from './form.service';
import { MembersService } from './members.service';
import { CheckoutModel } from '../models/application/paymentOptions';
import { ApiVersions } from './api-version';
import { IPaymentPreview } from '../models/application/applicationSubmit';
import { getErrorMessage } from './error-handler-factory.service';

@Injectable({ providedIn: 'root' })
export class ApplicationService extends BaseService {
  private basePath: string = 'applications';
  /** used for setting the html title */
  public readonly application = new BehaviorSubject<Application>(null);

  constructor(
    private formService: FormService,
    private membersService: MembersService
  ) {
    super();
  }

  async getBySecureLinkId(secureLinkId: string, personId: string): Promise<Application> {
    let application: Application;
    try {
      application = await lastValueFrom(
        this.get(
          `${this.basePath}/bySecureLink/${secureLinkId}/request`,
          false,
          ApiVersions.v0_1,
          true
        )
      ).then((data) => Application.parse(data));
    } catch (error) {
      if (error.status == 404 || error.status == 403) {
        return; //assuming base service handles 404 and 403
      }

      console.error('Error fetching application by secure link', error);

      //this.router.navigate([`${this.appConfigService.config.baseRoute}/not-found`]);
      return;
    }
    if (!application) {
      console.error('Error fetching application by secure link');
      this.router.navigate([`${this.appConfigService.config.baseRoute}/not-found`]);
      return;
    }

    if (application.form.isPrivate && !this.appConfigService.config.isPortal) {
      location.href = `/portal/forms/${personId}/application/${secureLinkId}`;
      return;
    }

    const navigationPath: string = this.appConfigService.config.baseRoute
      ? `${this.appConfigService.config.baseRoute}/forms`
      : 'members'; //default

    if (application.hasExpired) {
      this.router.navigate([`${navigationPath}/closed`]);
      return;
    }
    this.application.next(application);
    this.formService.setNext(application.form);
    this.membersService.workspaceLogoUrl.next(application.form.workspaceLogoUrl);
    this.membersService.workspaceHelpLink.next(application.form.workspaceHelpLink);
    return application;
  }

  getCheckoutOptions = (id: string, paymentPreview: IPaymentPreview): Promise<CheckoutModel> =>
    lastValueFrom(
      this.post(
        `${this.basePath}/bySecureLink/${id}/paymentOptions`,
        paymentPreview,
        null,
        ApiVersions.v0_1,
        true
      )
    );

  getCheckoutOptionsForPreview = (
    id: string,
    paymentPreview: IPaymentPreview
  ): Promise<CheckoutModel> =>
    lastValueFrom(
      this.post(
        `${this.basePath}/${id}/preview/paymentOptions`,
        paymentPreview,
        null,
        ApiVersions.v0_1,
        true
      )
    );

  async getForPreview(id: string): Promise<Application> {
    let path = `${this.basePath}/${id}/preview`;
    const data = await lastValueFrom(this.get(path));

    const application = Application.parse(data);
    this.membersService.workspaceLogoUrl.next(application.form.workspaceLogoUrl);
    return application;
  }

  sendBulkApplications(
    personIds: string[],
    formId: string,
    sendAll: boolean = true,
    sendNew: boolean = true,
    sendReminder: boolean = true
  ): Observable<any> {
    let body = JSON.stringify({
      formId: formId,
      personIds: personIds,
      sendAll: sendAll,
      sendNew: sendNew,
      sendReminder: sendReminder
    });
    let path = `${this.basePath}/send/bulk`;
    return this.post(path, body);
  }
  async sendBulkApplicationsToManagers(
    personIds: string[],
    formId: string,
    teamId: string,
    emailCustomization: {
      contactName: string;
      contactEmail: string;
      replyTo: string;
      subject: string;
      content: string;
    }
  ): Promise<boolean> {
    if (!teamId) {
      //if we ever change routing getting team id is brittle. So handle it as gracefully as possible.
      this.openSnackBar("Unable to retrieve current Team's Id");
      return false;
    }
    const body = JSON.stringify({
      formId,
      personIds,
      emailCustomization: {
        contactName: emailCustomization.contactName,
        contactEmail: emailCustomization.contactEmail,
        replyTo: emailCustomization.replyTo,
        subject: emailCustomization.subject,
        content: emailCustomization.content
      }
    });
    const path = `${this.basePath}/send/bulk-managers/${teamId}`;
    try {
      return await firstValueFrom(this.post(path, body, null, ApiVersions.v0_1, true));
    } catch (error) {
      const message = getErrorMessage(
        error,
        'There was an error sending the firm form to team managers.'
      );

      this.openSnackBar(message, false);

      return false;
    }
  }

  bulkApplicationsDryRun(
    personIds: string[],
    formId: string,
    sendAll: boolean = true,
    sendNew: boolean = true,
    sendReminder: boolean = true
  ): Observable<ApplicationSendResponse> {
    let body = JSON.stringify({
      formId: formId,
      personIds: personIds,
      sendAll: sendAll,
      sendNew: sendNew,
      sendReminder: sendReminder
    });
    let path = `${this.basePath}/send/bulk-dry-run`;
    return this.post(path, body).pipe(map(ApplicationSendResponse.parse));
  }
}
